import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  testItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '1px',
    padding: '16px 20px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #f0f0f0',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&:hover': {
      backgroundColor: '#fafafa',
      borderBottomColor: '#e0e0e0',
    },
    '&.test-passed': {
      backgroundColor: '#ffffff',
      borderLeft: '3px solid #10b981',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        backgroundColor: '#10b981',
      },
    },
    '&.test-failed': {
      backgroundColor: '#ffffff',
      borderLeft: '3px solid #ef4444',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        backgroundColor: '#ef4444',
      },
    },
  },
  testContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginRight: '24px',
  },
  testDescription: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1f2937',
    lineHeight: 1.5,
    marginBottom: '6px',
    letterSpacing: '-0.01em',
  },
  testActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  testStatusButtons: {
    display: 'flex',
    gap: '6px',
  },
  statusButton: {
    padding: '6px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: '70px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: '-0.005em',
    '&:hover': {
      backgroundColor: '#f9fafb',
      borderColor: '#9ca3af',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  passedButton: {
    color: '#059669',
    '&.active': {
      backgroundColor: '#059669',
      borderColor: '#059669',
      color: '#ffffff',
      boxShadow: '0 1px 3px rgba(5, 150, 105, 0.3)',
    },
    '&:hover:not(.active)': {
      backgroundColor: '#f0fdf4',
      borderColor: '#059669',
    },
  },
  failedButton: {
    color: '#dc2626',
    '&.active': {
      backgroundColor: '#dc2626',
      borderColor: '#dc2626',
      color: '#ffffff',
      boxShadow: '0 1px 3px rgba(220, 38, 38, 0.3)',
    },
    '&:hover:not(.active)': {
      backgroundColor: '#fef2f2',
      borderColor: '#dc2626',
    },
  },
  expectedResult: {
    color: '#6b7280',
    fontWeight: 400,
    fontSize: '12px',
    marginTop: '2px',
    lineHeight: 1.4,
    letterSpacing: '-0.005em',
  },
  commentField: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    marginTop: '8px',
    resize: 'vertical',
    minHeight: '60px',
    fontFamily: 'inherit',
    backgroundColor: '#fafafa',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    '&::placeholder': {
      color: '#9ca3af',
    },
  },
  commentLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 500,
    marginTop: '12px',
    marginBottom: '6px',
    letterSpacing: '-0.005em',
  },
  commentToggle: {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: '#6b7280',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: '#f9fafb',
      borderColor: '#9ca3af',
      color: '#374151',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    '&.active': {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      color: '#ffffff',
      boxShadow: '0 1px 3px rgba(59, 130, 246, 0.3)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
}));

const TestItem = ({
  testId,
  sectionId,
  description,
  expectedResult
}) => {
  const classes = useStyles();
  const [currentStatus, setCurrentStatus] = useState('');
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);

  const getStorageKey = (baseKey) => {
    const mode = window.currentTestingMode || 'feature';
    return `${baseKey}-${mode}`;
  };

  const loadTestState = () => {
    // Load initial status and comment from localStorage on component mount
    const saved = localStorage.getItem(getStorageKey('qhs-testing-progress'));
    if (saved) {
      const states = JSON.parse(saved);
      if (states[testId]) {
        const savedData = states[testId];
        if (typeof savedData === 'string') {
          // Old format compatibility
          setCurrentStatus(savedData);
        } else {
          // New format with status and comment
          setCurrentStatus(savedData.status || '');
          setComment(savedData.comment || '');
          // Show comment field if there's already a comment
          setShowComment(!!(savedData.comment && savedData.comment.trim()));
        }

        // Apply CSS classes after component mounts
        setTimeout(() => {
          const testItem = document.querySelector(`[data-test="${testId}"]`);
          if (testItem) {
            testItem.classList.remove('test-passed', 'test-failed');
            const status = savedData.status || savedData;
            if (status) {
              testItem.classList.add(`test-${status}`);
              testItem.setAttribute('data-status', status);
            } else {
              testItem.removeAttribute('data-status');
            }
          }
        }, 50);
      } else {
        // No saved data for this test in current mode - clear state
        setCurrentStatus('');
        setComment('');
        setShowComment(false);

        // Clear visual state
        setTimeout(() => {
          const testItem = document.querySelector(`[data-test="${testId}"]`);
          if (testItem) {
            testItem.classList.remove('test-passed', 'test-failed');
            testItem.removeAttribute('data-status');
          }
        }, 50);
      }
    } else {
      // No saved data at all for current mode - clear state
      setCurrentStatus('');
      setComment('');
      setShowComment(false);

      // Clear visual state
      setTimeout(() => {
        const testItem = document.querySelector(`[data-test="${testId}"]`);
        if (testItem) {
          testItem.classList.remove('test-passed', 'test-failed');
          testItem.removeAttribute('data-status');
        }
      }, 50);
    }
  };

  useEffect(() => {
    loadTestState();
  }, [testId]);

  useEffect(() => {
    // Listen for reset events and mode changes
    const handleReset = () => {
      setCurrentStatus('');
      setComment('');
      setShowComment(false);
    };

    const handleModeChange = () => {
      // Reload test state when mode changes
      loadTestState();
    };

    window.addEventListener('resetTests', handleReset);
    window.addEventListener('modeChanged', handleModeChange);
    return () => {
      window.removeEventListener('resetTests', handleReset);
      window.removeEventListener('modeChanged', handleModeChange);
    };
  }, []);

  const handleStatusChange = (status) => {
    // Toggle functionality - if clicking the same status, deselect it
    const newStatus = currentStatus === status ? '' : status;

    const testItem = document.querySelector(`[data-test="${testId}"]`);
    if (testItem) {
      // Remove all status classes
      testItem.classList.remove('test-passed', 'test-failed');

      // Add new status class
      if (newStatus) {
        testItem.classList.add(`test-${newStatus}`);
      }

      // Update the data attribute
      testItem.setAttribute('data-status', newStatus || '');

      // Update local state
      setCurrentStatus(newStatus);

      // Trigger save and progress update
      if (window.saveTestStates) {
        window.saveTestStates();
      }
      if (window.updateAllSectionProgress) {
        window.updateAllSectionProgress();
      }
    }
  };

  const handleCommentChange = (e) => {
    const newComment = e.target.value;
    setComment(newComment);

    // Trigger save
    if (window.saveTestStates) {
      window.saveTestStates();
    }
  };

  const toggleCommentField = () => {
    setShowComment(!showComment);
  };

  return (
    <div
      className={`${classes.testItem} test-item`}
      data-test={testId}
      data-section={sectionId}
      data-status={currentStatus}
    >
      <div className={classes.testContent}>
        <div className={`${classes.testDescription} testDescription`}>{description}</div>
        <span className={classes.expectedResult}>Expected: {expectedResult}</span>
        {showComment && (
          <>
            <div className={classes.commentLabel}>Comments:</div>
            <textarea
              className={classes.commentField}
              value={comment}
              onChange={handleCommentChange}
              placeholder="Add any comments about this test..."
              data-test-comment={testId}
            />
          </>
        )}
      </div>
      <div className={classes.testActions}>
        <button
          className={`${classes.commentToggle} ${showComment ? 'active' : ''}`}
          onClick={toggleCommentField}
          type="button"
          title={showComment ? 'Hide comments' : 'Add comments'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14l4-4h10a2 2 0 0 0 2-2V8"/>
            <path d="M18 2h-3a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h3l2 2V4a2 2 0 0 0-2-2z"/>
          </svg>
        </button>
        <div className={classes.testStatusButtons}>
          <button
            className={`${classes.statusButton} ${classes.passedButton} ${currentStatus === 'passed' ? 'active' : ''}`}
            onClick={() => handleStatusChange('passed')}
            type="button"
          >
            Pass
          </button>
          <button
            className={`${classes.statusButton} ${classes.failedButton} ${currentStatus === 'failed' ? 'active' : ''}`}
            onClick={() => handleStatusChange('failed')}
            type="button"
          >
            Fail
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestItem;
